import { useCallback, useEffect, useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { Input } from "@base-ui/react/input"
import { toast } from "sonner"
import {
  AuthShell,
  OtpInput,
  PrimaryButton,
  TextLink,
  fieldCls,
  labelCls,
} from "@/components/auth/AuthShell"
import {
  ensureSession,
  getSessionUser,
  sendEmailOtp,
  signInWithOtp,
  signOut,
  updateDisplayName,
} from "@/core/auth"
import { passkeySupport, registerPasskey } from "@/core/auth-client"
import {
  acceptInvitation,
  AuthFetchError,
  getInvitation,
  getOrgInvitePreview,
  rejectInvitation,
  type InvitationDetail,
} from "@/core/invitations"
import { resetEventsStore, useEvents } from "@/core/active-event"
import { resetUserOrgsStore, useUserOrgs } from "@/core/orgs"

// Native org-invitation acceptance for the aaa-2 console (replaces the old
// admin SPA's page). Public route — runs its own email-OTP sign-in, loads +
// accepts, then prompts to SECURE the account: a passkey (preferred) with a
// set-a-password fallback, or skip into the console.
type Stage =
  | "loading"
  | "signin-email"
  | "signin-otp"
  | "review"
  | "secure"
  | "already-accepted"
  | "declined"
  | "wrong-account"
  | "expired"
  | "error"

type Preview = { organizationName: string | null; role: string | null }

export const Route = createFileRoute("/accept-invitation/$invitationId")({
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  component: AcceptInvitation,
})

function AcceptInvitation() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { invitationId } = Route.useParams()
  const { email: emailFromUrl } = Route.useSearch()

  const [stage, setStage] = useState<Stage>("loading")
  const [invitation, setInvitation] = useState<InvitationDetail | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [errorMsg, setErrorMsg] = useState("")
  const [email, setEmail] = useState(emailFromUrl?.trim() ?? "")
  const [emailLocked, setEmailLocked] = useState(Boolean(emailFromUrl?.trim()))
  const [otp, setOtp] = useState("")
  const [name, setName] = useState("")
  const [busy, setBusy] = useState(false)
  const [resending, setResending] = useState(false)

  const loadInvitation = useCallback(async () => {
    if (!invitationId) {
      setErrorMsg("This invitation link is missing its code.")
      setStage("error")
      return
    }
    setStage("loading")
    try {
      const inv = await getInvitation(invitationId)
      setInvitation(inv)
      setStage("review")
    } catch (err) {
      if (err instanceof AuthFetchError) {
        if (err.status === 401) return void startSignIn()
        if (err.status === 403) return void setStage("wrong-account")
        if (err.status === 400) {
          // BA 400s for accepted / expired / revoked alike — ask the public
          // preview which it is so we can show the right terminal screen.
          const p = await getOrgInvitePreview(invitationId)
          if (p.status === "accepted") {
            setPreview({ organizationName: p.organizationName, role: null })
            return void setStage("already-accepted")
          }
          return void setStage("expired")
        }
      }
      setErrorMsg(err instanceof Error ? err.message : "Couldn't load invitation")
      setStage("error")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId])

  const startSignIn = useCallback(async () => {
    let haveEmail = Boolean(emailFromUrl?.trim())
    if (invitationId) {
      const p = await getOrgInvitePreview(invitationId)
      if (p.status === "pending") {
        setEmail((cur) => cur || p.email)
        setEmailLocked(true)
        setPreview({ organizationName: p.organizationName, role: p.role })
        haveEmail = true
      } else if (p.status === "expired" || p.status === "revoked") {
        setPreview({ organizationName: p.organizationName, role: null })
        return void setStage("expired")
      } else if (p.status === "accepted") {
        setPreview({ organizationName: p.organizationName, role: null })
        return void setStage("already-accepted")
      }
    }
    setStage(haveEmail ? "signin-email" : "signin-email")
  }, [invitationId, emailFromUrl])

  useEffect(() => {
    void (async () => {
      if (await ensureSession()) await loadInvitation()
      else await startSignIn()
    })()
  }, [loadInvitation, startSignIn])

  async function onSendOtp(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setBusy(true)
    try {
      await sendEmailOtp(email)
      toast.success(`Code sent to ${email}`)
      setStage("signin-otp")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send code")
    } finally {
      setBusy(false)
    }
  }

  async function onVerifyOtp(e?: React.FormEvent, code?: string) {
    e?.preventDefault()
    const value = code ?? otp
    if (!email || value.length !== 6 || busy) return
    setBusy(true)
    try {
      await signInWithOtp(email, value)
      queryClient.clear()
      resetEventsStore()
      resetUserOrgsStore()
      await loadInvitation()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "That code didn't work")
    } finally {
      setBusy(false)
    }
  }

  async function onResend() {
    if (!email) return
    setResending(true)
    try {
      await sendEmailOtp(email)
      toast.success(`New code sent to ${email}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't resend")
    } finally {
      setResending(false)
    }
  }

  async function onAccept() {
    if (!invitation) return
    const needsName = !getSessionUser()?.name?.trim()
    if (needsName && !name.trim()) {
      toast.error("Please enter your name")
      return
    }
    setBusy(true)
    try {
      if (needsName && name.trim()) await updateDisplayName(name.trim())
      await acceptInvitation(invitation.id)
      resetEventsStore()
      resetUserOrgsStore()
      await Promise.allSettled([
        useUserOrgs.getState().reload(),
        useEvents.getState().load(),
      ])
      setStage("secure")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't accept invitation")
    } finally {
      setBusy(false)
    }
  }

  async function onDecline() {
    if (!invitation) return
    setBusy(true)
    try {
      await rejectInvitation(invitation.id)
      setStage("declined")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't decline invitation")
    } finally {
      setBusy(false)
    }
  }

  async function onSwitchAccount() {
    setBusy(true)
    try {
      await signOut()
    } finally {
      queryClient.clear()
      resetEventsStore()
      resetUserOrgsStore()
      setOtp("")
      setBusy(false)
      setStage("signin-email")
    }
  }

  function goToConsole() {
    // Root is org-less and resolves to the user's org once useUserOrgs has
    // reloaded (aaa-2 has no bare /$orgSlug leaf to target directly).
    void navigate({ to: "/", replace: true })
  }

  async function onSetupPasskey() {
    setBusy(true)
    try {
      await registerPasskey()
      toast.success("Passkey saved — next time it's one tap.")
      goToConsole()
    } catch (err) {
      // Includes user-cancel; never block entry on it.
      toast.error(err instanceof Error ? err.message : "Couldn't set up a passkey")
      setBusy(false)
    }
  }

  const orgName = invitation?.organizationName ?? preview?.organizationName ?? null
  const role = invitation?.role ?? preview?.role ?? null
  const canPasskey = passkeySupport() === "available"

  return (
    <AuthShell title={orgName ? `Join ${orgName}` : "Accept invitation"} wave>
      {stage === "loading" && (
        <p className="py-6 text-center text-[13px] text-white/60">Loading your invitation…</p>
      )}

      {(stage === "signin-email" || stage === "signin-otp" || stage === "review") && (
        <InviteHeader orgName={orgName} role={role} />
      )}

      {stage === "signin-email" && (
        <form onSubmit={onSendOtp} className="mt-5 space-y-4">
          <div>
            <label htmlFor="email" className={`${labelCls} text-center`}>
              {emailLocked ? "Confirm it's you" : "Email the invitation was sent to"}
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus={!emailLocked}
              readOnly={emailLocked}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={fieldCls}
            />
            <p className="mt-2 text-center text-[12px] text-white/55">
              We'll email a 6-digit code to confirm it's you.
            </p>
          </div>
          <PrimaryButton disabled={busy || !email}>{busy ? "Sending…" : "Continue"}</PrimaryButton>
        </form>
      )}

      {stage === "signin-otp" && (
        <form onSubmit={onVerifyOtp} className="mt-5 space-y-4">
          <div>
            <label className={`${labelCls} text-center`}>Enter your code</label>
            <OtpInput value={otp} onChange={setOtp} autoFocus onComplete={(v) => void onVerifyOtp(undefined, v)} />
            <p className="mt-2 text-center text-[12px] text-white/55">
              Sent to <span className="font-medium text-white/80">{email}</span>
            </p>
          </div>
          <PrimaryButton disabled={busy || otp.length !== 6}>
            {busy ? "Verifying…" : "Continue"}
          </PrimaryButton>
          <div className="flex items-center justify-between">
            <TextLink onClick={() => { setOtp(""); setStage("signin-email") }}>← Back</TextLink>
            <TextLink disabled={resending} onClick={onResend}>
              {resending ? "Resending…" : "Resend code"}
            </TextLink>
          </div>
        </form>
      )}

      {stage === "review" && invitation && (
        <div className="mt-5 space-y-4">
          <p className="text-center text-[12px] text-white/55">
            Signed in as <span className="font-medium text-white/80">{getSessionUser()?.email ?? email}</span>
          </p>
          {!getSessionUser()?.name?.trim() && (
            <div>
              <label htmlFor="name" className={`${labelCls} text-center`}>Your name</label>
              <Input
                id="name"
                autoComplete="name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={fieldCls}
              />
            </div>
          )}
          <PrimaryButton onClick={onAccept} disabled={busy}>
            {busy ? "Joining…" : "Accept & join"}
          </PrimaryButton>
          <div className="text-center">
            <TextLink disabled={busy} onClick={onDecline}>Decline invitation</TextLink>
          </div>
        </div>
      )}

      {stage === "secure" && (
        <div className="space-y-4 text-center">
          <div className="text-3xl">🎉</div>
          <div className="space-y-1">
            <p className="text-[15px] font-semibold text-white">You're in</p>
            <p className="text-[13px] text-white/60">
              Secure your account so signing in next time is one tap.
            </p>
          </div>
          {canPasskey && (
            <PrimaryButton onClick={onSetupPasskey} disabled={busy}>
              {busy ? "Setting up…" : "Set up a passkey"}
            </PrimaryButton>
          )}
          <TextLink onClick={goToConsole}>{canPasskey ? "Skip for now" : "Continue"}</TextLink>
          <div className="pt-1">
            <TextLink
              onClick={() =>
                void navigate({ to: "/set-password", search: { email: getSessionUser()?.email ?? email } })
              }
            >
              Prefer a password? Set one up
            </TextLink>
          </div>
        </div>
      )}

      {stage === "already-accepted" && (
        <Outcome
          title="You're already in"
          body={`You've already joined ${orgName ?? "this team"}.`}
          ctaLabel="Go to Attend"
          onCta={() => void navigate({ to: "/" })}
        />
      )}

      {stage === "declined" && (
        <Outcome title="Invitation declined" body="You can safely close this page." ctaLabel="Go to Attend" onCta={() => void navigate({ to: "/" })} />
      )}

      {stage === "wrong-account" && (
        <Outcome
          title="Wrong account"
          body={`You're signed in as ${getSessionUser()?.email ?? "someone else"}, but this invitation was sent to a different address. Sign out and continue with the invited email.`}
          ctaLabel={busy ? "Signing out…" : "Sign out & continue"}
          onCta={onSwitchAccount}
          disabled={busy}
        />
      )}

      {stage === "expired" && (
        <Outcome
          title="Invitation unavailable"
          body="This invitation is no longer valid — it may have expired or been revoked. Ask whoever invited you to send a new one."
          ctaLabel="Go to Attend"
          onCta={() => void navigate({ to: "/" })}
        />
      )}

      {stage === "error" && (
        <Outcome
          title="Something went wrong"
          body={errorMsg || "We couldn't load this invitation."}
          ctaLabel="Try again"
          onCta={() => void (async () => {
            if (await ensureSession()) await loadInvitation()
            else await startSignIn()
          })()}
        />
      )}
    </AuthShell>
  )
}

function InviteHeader({ orgName, role }: { orgName: string | null; role: string | null }) {
  return (
    <div className="text-center">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/45">
        You're invited to join
      </p>
      <h1 className="mt-1.5 text-[20px] font-semibold tracking-tight text-white">
        {orgName ?? "an organization"}
      </h1>
      {role && (
        <span className="mt-2.5 inline-flex items-center rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-[12px] font-medium capitalize text-white/85">
          {role}
        </span>
      )}
    </div>
  )
}

function Outcome({
  title,
  body,
  ctaLabel,
  onCta,
  disabled,
}: {
  title: string
  body: string
  ctaLabel: string
  onCta: () => void
  disabled?: boolean
}) {
  return (
    <div className="space-y-4 text-center">
      <div className="space-y-1.5">
        <h1 className="text-[16px] font-semibold tracking-tight text-white">{title}</h1>
        <p className="text-[13px] leading-relaxed text-white/60">{body}</p>
      </div>
      <PrimaryButton onClick={onCta} disabled={disabled}>{ctaLabel}</PrimaryButton>
    </div>
  )
}
